/**
 * `/admin/settings` — `app_settings`, which holds exactly four things: the §8
 * refusal text, the current export-policy version, the attachment size cap and
 * the attachment content-type allowlist.
 *
 * **Bumping the policy version re-prompts everyone**, immediately and on their
 * next request — including the Admin doing the bumping. That is the intended
 * behaviour, and the screen says so before the change rather than after.
 *
 * Archives stay off the allowlist: a `.zip` defeats an allowlist entirely.
 */

import { useState } from 'react';
import { useApiMutation, useApiQuery } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { REFERENCE_QUERY_KEY } from '@/lib/reference';
import { ME_QUERY_KEY } from '@/auth/useMe';
import { formatDateTime } from '@/lib/format';

interface SettingRow {
  key: string;
  value: unknown;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
}

const SETTINGS_KEY = ['admin', 'settings'] as const;

const HINTS: Record<string, string> = {
  export_policy_version:
    'Change this and every user, including you, is blocked at the acknowledgment screen until they accept again.',
  export_refusal_text:
    'Shown when someone answers yes, or is not sure, to the export question. It names the approved system or process.',
  attachment_max_bytes:
    'The hard ceiling on an upload. The presigned POST policy derives its own bound from this, so the two cannot drift.',
  attachment_content_type_allowlist:
    'Exact content types. Archives are excluded deliberately — a .zip defeats an allowlist entirely.',
};

export function AdminSettings(): React.ReactElement {
  const settings = useApiQuery<{ items: SettingRow[] }>(SETTINGS_KEY, '/api/admin/settings');

  return (
    <div className="space-y-4">
      {(settings.data?.items ?? []).map((setting) => (
        <SettingEditor key={setting.key} setting={setting} />
      ))}
      {settings.error !== null && (
        <p className="rounded-md border border-destructive/40 bg-red-50 p-3 text-sm text-red-900">
          {settings.error.message}
        </p>
      )}
    </div>
  );
}

function SettingEditor({ setting }: { setting: SettingRow }): React.ReactElement {
  const [draft, setDraft] = useState(() => JSON.stringify(setting.value, null, 2));
  const [confirming, setConfirming] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const save = useApiMutation<{ updated: boolean }, void>(
    () => ({
      path: `/api/admin/settings/${setting.key}`,
      method: 'PUT',
      body: { value: JSON.parse(draft) as unknown },
    }),
    {
      invalidate: [SETTINGS_KEY, REFERENCE_QUERY_KEY, ME_QUERY_KEY],
    },
  );

  const attempt = (): void => {
    try {
      JSON.parse(draft);
      setParseError(null);
    } catch {
      setParseError('That is not valid JSON. A string needs its quotes.');
      return;
    }
    if (setting.key === 'export_policy_version') setConfirming(true);
    else save.mutate();
  };

  return (
    <section className="rounded-lg border bg-card p-4">
      <h2 className="text-sm font-medium">{setting.key}</h2>
      <p className="mb-2 text-xs text-muted-foreground">
        {HINTS[setting.key] ?? setting.description ?? ''}
      </p>

      <Field label="Value (JSON)">
        <Textarea
          rows={setting.key === 'export_refusal_text' ? 5 : 3}
          className="font-mono text-xs"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      </Field>

      {parseError !== null && <p className="mt-1 text-xs text-destructive">{parseError}</p>}

      <div className="mt-2 flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">
          Last changed {formatDateTime(setting.updated_at)}. Every change is audited.
        </p>
        <Button size="sm" onClick={attempt} disabled={save.isPending}>
          Save
        </Button>
      </div>

      {confirming && (
        <Dialog
          open
          size="sm"
          onClose={() => setConfirming(false)}
          title="This re-prompts everyone"
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setConfirming(false);
                  save.mutate();
                }}
              >
                Bump the version
              </Button>
            </>
          }
        >
          <p className="text-sm">
            Every user is refused on every route until they acknowledge the new version — including
            you, on your next request. They will see the acknowledgment screen and nothing else.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            That is the point of the version: it is how a change to the policy reaches people who
            already accepted the old one.
          </p>
        </Dialog>
      )}
    </section>
  );
}
